import { useEffect, useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { Pager, usePager } from "@/components/ui/Pager";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { AssigneeField, assignOrderTasks } from "./Assignee";
import { LpLabels } from "@/modules/lp/LpLabels";
import { NewOrder } from "./NewOrder";
import { ReleasePallets } from "./ReleasePallets";
import { OrderLinesTable, useOrderDetail, type PickingLine } from "./OrderLines";
import { downloadFile } from "@/lib/api/files";
import { useAction, useItem, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, relative, text } from "@/lib/format";

/**
 * 1C chiqim buyurtmalari (zakas).
 *
 * Dispetcher buyurtmani «Terishga chiqarish» bilan rezervlaydi;
 * teruvchilarga topshiriqlar shundan keyin tushadi. Qoldiq yetmagan
 * qism ochiq ko'rsatiladi (FIX-05).
 *
 * Kirim (prixod) 1C hujjatidan EMAS: xodim TSD da erkin qabul
 * sessiyasini o'zi boshlaydi, nakladnoy esa «Qabul sessiyalari»
 * sahifasida biriktiriladi (buyurtmachi qarori, 2026-11-01).
 */

interface PickingOrder {
  id: string;
  order_number: string;
  external_id?: string;
  customer_id?: string;
  /**
   * 1C «Склад получатель» — yuk qaysi omborga ketadi. Server har doim
   * beradi, eski buyurtmalarda bo'sh.
   */
  recipient_warehouse?: string;
  /** Terishga chiqarishda biriktirilgan tara. */
  lp_id?: string;
  lp_code?: string;
  warehouse_code: string;
  status: string;
  priority?: number;
  due_at?: string;
  incomplete_reason?: string;
  cancelled_reason?: string;
  /**
   * Zakas 1C da yopilgan (chiqim ordeni o'tkazilgan yoki nakladnoy
   * o'chirilgan). Terishga chiqmagani avtomatik bekor qilinadi
   * (`cancelled_code: closed_in_1c`); terilayotgani — ogohlantirish.
   */
  closed_in_1c_at?: string;
  closed_in_1c_reason?: string;
  closed_in_1c_ref?: string;
  cancelled_code?: string;
  version: number;
  created_at: string;
  /** Faqat bitta buyurtma so'ralganda keladi — ro'yxatda yo'q. */
  lines?: PickingLine[];
}

/** «1C da yopilgan» izohi: chiqim orderi raqami yoki sabab. */
function closedIn1CText(order: PickingOrder): string {
  if (order.closed_in_1c_reason === "DELETED") return "1C da nakladnoy o'chirilgan";
  if (order.closed_in_1c_reason === "UNPOSTED") return "1C da nakladnoy o'tkazilmagan";
  return order.closed_in_1c_ref ? `1C da chiqim orderi ${order.closed_in_1c_ref}` : "1C da yopilgan";
}

/**
 * «1C da yopilgan» nishoni: bekor qilingan (avtomatik) — oddiy,
 * terilayotgan — ogohlantirish (terish WMS da davom etmoqda, 1C esa
 * yukni allaqachon chiqargan).
 */
function ClosedIn1CBadge({ order }: { order: PickingOrder }) {
  if (!order.closed_in_1c_at) return null;
  const cancelled = order.status === "CANCELLED";
  return (
    <span title={`${closedIn1CText(order)} · ${dateTime(order.closed_in_1c_at)}`}>
      <StatusBadge value={cancelled ? "1C da yopilgan" : "1C da yopilgan!"} tone={cancelled ? "neutral" : "warning"} />
    </span>
  );
}

interface Counterparty {
  id: string;
  name: string;
}

// `closed1c` — 1C da yopilgan zakaslar: bekor qilinganlari «Faol» da
// ko'rinmaydi, ularni shu filtr ko'rsatadi.
type Scope = "active" | "all" | "closed1c";

/**
 * Bitta sahifadagi buyurtmalar soni.
 *
 * Ro'yxat SAHIFALANADI: 1C hujjatlari to'planib boradi va ilgari
 * birinchi 200 tasidan keyingisiga yo'l yo'q edi.
 */
const PAGE_SIZE = 50;

/**
 * Bekor qilinadigan holatlar — server holat mashinasi bilan BIR XIL
 * (picking `orderTransitions`). Nazoratga uzatilgan buyurtma endi
 * terishniki emas: uni bekor qilish nazorat/jo'natish orqali.
 */
const CANCELLABLE = new Set(["CREATED", "ALLOCATED", "IN_PROGRESS", "PICKED", "INCOMPLETE"]);

export function Orders() {
  const session = useSession();
  const [scope, setScope] = useState<Scope>("active");
  const [search, setSearch] = useState("");
  const [releasing, setReleasing] = useState<PickingOrder | null>(null);
  // Ixtiyoriy ijrochi: bo'sh qolsa ish avtomatik taqsimlanadi.
  const [assignee, setAssignee] = useState("");
  // Biriktirish hujjat ishga qo'yilgandan KEYIN bo'ladi, shuning
  // uchun uning xatosi alohida: hujjat baribir chiqarilgan.
  const [assignError, setAssignError] = useState<string | undefined>();
  const [handing, setHanding] = useState<PickingOrder | null>(null);
  const [cancelling, setCancelling] = useState<PickingOrder | null>(null);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [docError, setDocError] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const [handoffNote, setHandoffNote] = useState("");
  const [opened, setOpened] = useState<PickingOrder | null>(null);

  // Qidiruv SERVERDA: sahifalangan ro'yxatda brauzer faqat ochilgan
  // sahifani ko'radi va xodim «bunday buyurtma yo'q» deb o'ylardi.
  const [needle, setNeedle] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setNeedle(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // 1C «Склад получатель» filtri (faqat chiqim) — SERVERDA, aniq qiymat.
  const [recipient, setRecipient] = useState("");

  // Tab, ko'rinish yoki qidiruv o'zgarsa birinchi sahifaga qaytiladi.
  const pager = usePager(`${scope}|${needle}|${recipient}`);
  const listQuery = {
    limit: PAGE_SIZE,
    cursor: pager.cursor,
    q: needle || undefined,
    // «Faol» ham SERVERDA: yakunlangan buyurtmani brauzerda kesish
    // sahifani yarim bo'sh qoldirardi.
    only_open: scope === "active" ? true : undefined,
  };
  const closedQuery = scope === "closed1c" ? { closed_in_1c: true } : {};
  const recipients = useList<string>("picking", "/api/v1/picking/orders/recipient-warehouses", {});
  const outbound = useList<PickingOrder>("picking", "/api/v1/picking/orders", {
    ...listQuery,
    ...closedQuery,
    recipient_warehouse: recipient || undefined,
  });

  const allocate = useAction<PickingOrder, PickingOrder>(
    "picking",
    (order) => ({
      path: `/api/v1/picking/orders/${order.id}/allocate`,
      method: "POST",
    }),
  );

  // Bekor qilish SABAB bilan: u buyurtma tarixida va 1C ga ketadigan
  // natijada qoladi. Rezervlar yechiladi, ochiq topshiriqlar yopiladi.
  const cancel = useAction<PickingOrder, { order: PickingOrder; reason: string }>(
    "picking",
    ({ order, reason }) => ({
      path: `/api/v1/picking/orders/${order.id}/cancel`,
      method: "POST",
      body: { reason },
    }),
  );

  const handoff = useAction<PickingOrder, PickingOrder>("picking", (order) => ({
    path: `/api/v1/picking/orders/${order.id}/handoff-control`,
    method: "POST",
    body:
      order.status === "INCOMPLETE"
        ? { accept_incomplete: true, note: handoffNote.trim() }
        : { note: handoffNote.trim() || undefined },
  }));

  const canRelease =
    session.can(PERM.pickingAllocate) || session.can(PERM.pickingExecute);
  // FIX-05: to'liq bo'lmagan buyurtmani o'tkazish — menejer qarori.
  const canAcceptIncomplete = session.can(PERM.managerDecision);
  // Buyurtmani bekor qilish — dispetcher va ombor rahbari. Teruvchi
  // (`picking.execute`) butun buyurtmani bekor qila olmaydi.
  const canCancel = session.can(PERM.pickingOrderCancel);
  // Bo'sh LP/tara yorliqlari — smenaga tayyorgarlik (ofis).
  const canPrintLabels = session.can(PERM.inventoryMove) || session.can(PERM.taskAssign);
  // Qo'lda hujjat kiritish SERVERDA ham shu vakolat bilan ochiq
  // (1C bilan bir xil endpoint), shuning uchun tugma ham shunga
  // qarab ko'rsatiladi.
  const canCreate = session.can(PERM.pickingExecute);
  const outboundRows = outbound.data?.items ?? [];

  const outboundColumns: Array<Column<PickingOrder>> = [
    {
      key: "number",
      header: "Raqam",
      render: (row) => <span className="mono">{row.order_number}</span>,
    },
    {
      key: "customer",
      header: "Mijoz",
      render: (row) => <CounterpartyName id={row.customer_id} />,
    },
    {
      key: "recipient",
      header: "Qabul ombori",
      value: (row) => row.recipient_warehouse,
      render: (row) => text(row.recipient_warehouse),
    },
    {
      key: "due",
      header: "Muddat",
      value: (row) => row.due_at,
      render: (row) => (row.due_at ? dateTime(row.due_at) : "—"),
    },
    {
      key: "lp",
      header: "Tara (LP)",
      render: (row) =>
        row.lp_code ? <span className="mono">{row.lp_code}</span> : "—",
    },
    {
      key: "status",
      header: "Holat",
      render: (row) => (
        <span title={row.incomplete_reason} style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
          <StatusBadge value={row.status} />
          <ClosedIn1CBadge order={row} />
        </span>
      ),
    },
    {
      key: "created",
      header: "Kelgan",
      value: (row) => row.created_at,
      render: (row) => (
        <span title={dateTime(row.created_at)}>{relative(row.created_at)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <span style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 6 }}>
          <button
            type="button"
            className="btn btn-text btn-sm"
            onClick={() => setOpened(row)}
          >
            <Icon name="search" size={14} />
            SKU lar
          </button>
          {/* Nakladnoy — tara kodi shtrix-kod bo'lib chiqadi, shuning
              uchun u faqat tara biriktirilgandan keyin ma'noli. */}
          {row.lp_code && (
            <button
              type="button"
              className="btn btn-tonal btn-sm"
              onClick={() => {
                setDocError(undefined);
                downloadFile("picking", `/api/v1/picking/orders/${row.id}/document.pdf`,
                  `nakladnoy-${row.order_number}.pdf`)
                  .catch((cause: unknown) =>
                    setDocError(cause instanceof Error ? cause.message : String(cause)));
              }}
            >
              <Icon name="printer" size={14} />
              Nakladnoy
            </button>
          )}
          {canRelease &&
            (row.status === "CREATED" || row.status === "INCOMPLETE") && (
              <button
                type="button"
                className={
                  row.status === "CREATED" ? "btn btn-filled btn-sm" : "btn btn-tonal btn-sm"
                }
                onClick={() => setReleasing(row)}
              >
                <Icon name={row.status === "CREATED" ? "check" : "refresh"} size={14} />
                {row.status === "CREATED" ? "Terishga chiqarish" : "Qayta taqsimlash"}
              </button>
            )}
          {canRelease && row.status === "PICKED" && (
            <button
              type="button"
              className="btn btn-filled btn-sm"
              onClick={() => openHandoff(row)}
            >
              <Icon name="check" size={14} />
              Nazoratga uzatish
            </button>
          )}
          {canAcceptIncomplete && row.status === "INCOMPLETE" && (
            <button
              type="button"
              className="btn btn-text btn-sm"
              onClick={() => openHandoff(row)}
            >
              To'liq emas holida uzatish
            </button>
          )}
          {canCancel && CANCELLABLE.has(row.status) && (
            <button
              type="button"
              className="btn btn-text btn-sm btn-danger-text"
              onClick={() => {
                cancel.reset();
                setCancelling(row);
              }}
            >
              <Icon name="close" size={14} />
              Bekor qilish
            </button>
          )}
        </span>
      ),
    },
  ];

  function openHandoff(order: PickingOrder) {
    setHandoffNote("");
    setHanding(order);
  }

  /**
   * Hujjat ishga qo'yilgandan keyin topshiriqlarni biriktiradi.
   *
   * Xato bo'lsa oyna YOPILADI va ogohlantirish ro'yxat ustida
   * qoladi: hujjat allaqachon chiqarilgan va uni ortga qaytarib
   * bo'lmaydi, biriktirish esa ixtiyoriy qadam — u bajarilmasa
   * ish shunchaki avtomatik taqsimlanadi.
   */
  function afterRelease(orderId: string, taskType: string, close: () => void) {
    setAssignError(undefined);
    if (!assignee) {
      close();
      return;
    }
    assignOrderTasks(orderId, assignee, taskType)
      .catch((cause: unknown) =>
        setAssignError(
          `Hujjat chiqarildi, lekin xodimga biriktirilmadi: ${
            cause instanceof Error ? cause.message : String(cause)
          }. Ish navbatda qoladi.`,
        ),
      )
      .finally(close);
  }

  const active = outbound;

  return (
    <>
      <PageHeader
        title="Buyurtmalar"
        hint="Chiqim buyurtmalari — 1C dan yoki qo'lda. «Terishga chiqarish» bosilganda teruvchilarga topshiriq tushadi; teruvchi istalgan bo'sh palletni o'zi oladi."
        actions={
          <>
            {canCreate && (
              <button type="button" className="btn btn-filled" onClick={() => setCreating(true)}>
                <Icon name="plus" size={16} />
                Yangi zakas
              </button>
            )}
            {canPrintLabels && (
              <button type="button" className="btn btn-tonal" onClick={() => setLabelsOpen(true)}>
                <Icon name="printer" size={16} />
                LP / tara yorliqlari
              </button>
            )}
          </>
        }
      />

      {docError && (
        <div className="notice" role="alert" style={{ marginBottom: 12 }}>
          <Icon name="alert" size={16} />
          <span>{docError}</span>
        </div>
      )}

      {assignError && (
        <div className="notice" role="alert" style={{ marginBottom: 12 }}>
          <Icon name="alert" size={16} />
          <span>{assignError}</span>
        </div>
      )}

      <LpLabels open={labelsOpen} onClose={() => setLabelsOpen(false)} />
      <NewOrder open={creating} onClose={() => setCreating(false)} />

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Raqam, 1C kodi, mijoz, qabul qiluvchi ombor…"
        onRefresh={() => void active.refetch()}
        refreshing={active.isFetching}
      >
        {(recipients.data?.items.length ?? 0) > 0 && (
          <select
            className="input"
            style={{ width: "auto", maxWidth: 260 }}
            value={recipient}
            onChange={(event) => setRecipient(event.target.value)}
            aria-label="Qabul qiluvchi ombor (1C «Склад получатель»)"
            title="Qabul qiluvchi ombor (1C «Склад получатель»)"
          >
            <option value="">Barcha qabul qiluvchi omborlar</option>
            {recipients.data?.items.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        )}
        <SegmentedFilter
          value={scope}
          onChange={setScope}
          options={[
            { value: "active", label: "Faol" },
            { value: "all", label: "Hammasi" },
            { value: "closed1c", label: "1C da yopilgan" },
          ]}
        />
      </Toolbar>

        <DataTable
          columns={outboundColumns}
          rows={outboundRows}
          rowKey={(row) => row.id}
          isLoading={outbound.isLoading}
          error={outbound.error}
          onRetry={() => void outbound.refetch()}
          emptyTitle="Chiqim buyurtmasi yo'q"
          emptyHint="1C dan kelgan sotuv buyurtmalari (SSL) shu yerda ko'rinadi."
          hasMore={outbound.data?.has_more}
          footer={
            <Pager
              page={pager.page}
              atFirst={pager.atFirst}
              shown={outboundRows.length}
              hasMore={outbound.data?.has_more}
              onPrev={pager.prev}
              onNext={() => pager.next(outbound.data?.next_cursor)}
            />
          }
        />

      <ConfirmDialog
        open={releasing !== null}
        title={
          releasing
            ? `Terishga chiqarish: ${releasing.order_number}`
            : "Terishga chiqarish"
        }
        description="Qoldiq rezervlanadi va teruvchilarga topshiriqlar tushadi. Pallet berilmaydi — teruvchi istalgan bo'sh palletni olib, yorlig'ini skanerlaydi. Qoldiq yetmagan qism ochiq ko'rsatiladi — jim kamaytirilmaydi."
        confirmLabel="Chiqarish"
        busy={allocate.isPending}
        error={allocate.error}
        card
        extra={
          releasing ? (
            <div style={{ display: "grid", gap: 14 }}>
              <AssigneeField
                warehouseCode={releasing.warehouse_code}
                role="picker"
                value={assignee}
                onChange={setAssignee}
                hint="Tanlansa, buyurtmaning terish topshirig'i (barcha SKU lari bilan) shu xodimga tushadi."
              />
              <div>
                <div className="field-label" style={{ marginBottom: 6 }}>
                  Buyurtma tarkibi
                </div>
                <OrderLinesTable
                  orderId={releasing.id}
                  warehouseCode={releasing.warehouse_code}
                  showAvailable
                />
              </div>
              <ReleasePallets
                orderId={releasing.id}
                orderNumber={releasing.order_number}
              />
            </div>
          ) : null
        }
        onClose={() => {
          setReleasing(null);
          setAssignee("");
          allocate.reset();
        }}
        onConfirm={() => {
          if (!releasing) return;
          const order = releasing;
          allocate.mutate(order, {
            onSuccess: () => {
              // Tara yorlig'i chop etilmaydi (2026-11-01): pallet
              // berilmaydi, teruvchi oldindan chop etilgan istalgan bo'sh
              // palletni oladi.
              afterRelease(order.id, "PICK", () => {
                setReleasing(null);
                setAssignee("");
                allocate.reset();
              });
            },
          });
        }}
      />

      <ConfirmDialog
        open={cancelling !== null}
        title={cancelling ? `Buyurtmani bekor qilish: ${cancelling.order_number}` : "Buyurtmani bekor qilish"}
        description="Rezervlar yechiladi va ochiq terish topshiriqlari yopiladi. Allaqachon terilgan tovar joyiga qaytarish topshirig'i bilan qaytadi. Amalni ortga qaytarib bo'lmaydi."
        confirmLabel="Bekor qilish"
        danger
        requireReason
        reasonLabel="Sabab"
        reasonPlaceholder="Masalan: mijoz buyurtmani 1C da bekor qildi"
        busy={cancel.isPending}
        error={cancel.error}
        onClose={() => {
          setCancelling(null);
          cancel.reset();
        }}
        onConfirm={(reason) => {
          if (!cancelling) return;
          cancel.mutate(
            { order: cancelling, reason },
            { onSuccess: () => setCancelling(null) },
          );
        }}
      />

      <ConfirmDialog
        open={handing !== null}
        title={
          handing?.status === "INCOMPLETE"
            ? `To'liq emas holida uzatish: ${handing.order_number}`
            : `Nazoratga uzatish: ${handing?.order_number ?? ""}`
        }
        description={
          handing?.status === "INCOMPLETE"
            ? "Menejer qarori (FIX-05): yetmagan qism terilmaydi va buyurtma nazoratga shu holida o'tadi. Sababni yozing — u buyurtma tarixida qoladi."
            : "Terilgan tovar nazoratga o'tadi; nazoratchining TSD siga topshiriq tushadi."
        }
        confirmLabel="Uzatish"
        danger={handing?.status === "INCOMPLETE"}
        busy={handoff.isPending}
        error={handoff.error}
        confirmDisabled={handing?.status === "INCOMPLETE" && handoffNote.trim() === ""}
        extra={
          <label className="field">
            <span className="field-label">
              {handing?.status === "INCOMPLETE" ? "Sabab" : "Izoh (ixtiyoriy)"}
            </span>
            <input
              className="input"
              value={handoffNote}
              onChange={(event) => setHandoffNote(event.target.value)}
            />
          </label>
        }
        onClose={() => {
          setHanding(null);
          handoff.reset();
        }}
        onConfirm={() => {
          if (!handing) return;
          handoff.mutate(handing, {
            onSuccess: () => {
              setHanding(null);
              handoff.reset();
            },
          });
        }}
      />

      {opened && <OrderLines order={opened} onClose={() => setOpened(null)} />}
    </>
  );
}

/**
 * Kontragent nomi. Buyurtma ro'yxati faqat ID beradi; nom
 * master-data dan olinadi va kesh tufayli har kontragent bir marta
 * so'raladi.
 */
function CounterpartyName({ id }: { id?: string }) {
  const party = useItem<Counterparty>(
    "masterData",
    `/api/v1/counterparties/${id ?? ""}`,
    { enabled: Boolean(id) },
  );
  if (!id) return <>—</>;
  return (
    <>{party.data?.name ?? <span className="mono">{id.slice(0, 8)}</span>}</>
  );
}

/**
 * Buyurtma satrlari: tovar nomi bilan, reja va fakt.
 *
 * Ro'yxat javobida satrlar YO'Q — buyurtma alohida so'raladi.
 */
function OrderLines({ order, onClose }: { order: PickingOrder; onClose: () => void }) {
  const detail = useOrderDetail<PickingOrder>(order.id);
  const full = detail.data ?? order;
  const pickingOrder = full;

  return (
    <Drawer label="Buyurtma SKU lari">
      <div className="drawer-head">
        <div>
          <div className="dialog-title">{full.order_number}</div>
          <div className="dialog-desc">
            Chiqim · <CounterpartyName id={pickingOrder.customer_id} />{" "}
            · 1C: <span className="mono">{text(full.external_id)}</span> ·{" "}
            <StatusBadge value={full.status} />
          </div>
          <div className="dialog-desc">
            Qabul qiluvchi ombor (1C «Склад получатель»):{" "}
            <strong>{text(pickingOrder.recipient_warehouse)}</strong>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-icon"
          onClick={onClose}
          aria-label="Yopish"
        >
          <Icon name="close" />
        </button>
      </div>
      <div className="drawer-body">
        <OrderLinesTable orderId={order.id} warehouseCode={full.warehouse_code} />
        {pickingOrder.closed_in_1c_at && (
          <div className="notice" role="alert" style={{ marginBottom: 12 }}>
            <Icon name="alert" size={16} />
            <span>
              {closedIn1CText(pickingOrder)} ({dateTime(pickingOrder.closed_in_1c_at)}).{" "}
              {pickingOrder.status === "CANCELLED"
                ? "Zakas terishga chiqmagan edi — avtomatik bekor qilindi."
                : "Terish WMS da boshlangan — zakas bekor qilinmadi. Yukni 1C bilan solishtiring."}
            </span>
          </div>
        )}
        {pickingOrder.incomplete_reason && (
          <div className="notice" style={{ marginTop: 12 }}>
            To'liq emas: {pickingOrder.incomplete_reason}
          </div>
        )}
      </div>
    </Drawer>
  );
}
